import React, { useEffect, useState } from "react";
import DatePicker, {
  registerLocale,
  ReactDatePickerCustomHeaderProps,
} from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, Locale } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar } from "@fortawesome/free-solid-svg-icons/faCalendar";
import { getLocale } from "../../../utils";
import { useAppSelector } from "../../../app/hooks";

interface DateInputProps {
  id: string;
  date?: Date;
  onDateSelect: (arg0: Date) => void;
  inputLabel: string;
  title: string;
  error?: string;
  disabled: boolean;
  startingDay: number;
  minDate?: Date;
}

function DateInput({
  id,
  date,
  onDateSelect,
  inputLabel,
  title,
  error,
  disabled,
  startingDay,
  minDate,
}: DateInputProps) {
  const [locale, setLocale] = useState<Locale>();
  const [value, setValue] = useState<string>("");

  const language = useAppSelector((state) => state.config.app.language);
  const dateFormat = useAppSelector(
    (state) => state.config.components.dateFormat
  );

  useEffect(() => {
    const newLocale = getLocale(language);
    setLocale(newLocale);
    registerLocale(language, newLocale);
  }, [language]);

  useEffect(() => {
    if (date) {
      let formattedDate;
      if (locale?.code === "ar-SA") {
        formattedDate = format(date, "dd MMMM yyyy", { locale: locale });
      } else if (language === "ja") {
        // Format as "2025年12月9日" for Japanese
        formattedDate = `${date.getFullYear()}年${
          date.getMonth() + 1
        }月${date.getDate()}日`;
      } else {
        formattedDate = format(date, dateFormat, { locale: locale });
      }
      setValue(formattedDate);
    } else {
      setValue("");
    }
  }, [date, locale, dateFormat, language]);

  const renderCustomHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }: ReactDatePickerCustomHeaderProps) => {
    // For Japanese locale, format as "2025年12月"
    const monthYearText =
      language === "ja"
        ? `${date.getFullYear()}年${date.getMonth() + 1}月`
        : format(date, "MMMM yyyy", { locale: locale });

    return (
      <div className="react-datepicker__custom-header">
        <button
          type="button"
          onClick={decreaseMonth}
          disabled={prevMonthButtonDisabled}
          className="react-datepicker__navigation react-datepicker__navigation--previous"
          aria-label="Previous Month"
        >
          <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--previous">
            {"<"}
          </span>
        </button>
        <span className="react-datepicker__current-month">{monthYearText}</span>
        <button
          type="button"
          onClick={increaseMonth}
          disabled={nextMonthButtonDisabled}
          className="react-datepicker__navigation react-datepicker__navigation--next"
          aria-label="Next Month"
        >
          <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--next">
            {">"}
          </span>
        </button>
      </div>
    );
  };

  return (
    <div className="field" title={!disabled ? title : ""}>
      <label
        className={`label ${disabled ? "date-input-disabled" : ""}`}
        htmlFor={id}
      >
        {inputLabel}
      </label>
      <DatePicker
        locale={locale}
        selected={date}
        onChange={onDateSelect}
        calendarStartDay={startingDay as 0 | 1 | 2 | 3 | 4 | 5 | 6}
        minDate={minDate}
        inline={false}
        disabledKeyboardNavigation={false}
        renderCustomHeader={renderCustomHeader}
        customInput={
          <div className="control has-icons-left">
            <input
              id={id}
              className={`input ${error === null ? "" : "is-danger"}`}
              type="text"
              disabled={disabled}
              value={value}
              onChange={() => {}}
              placeholder={inputLabel}
            />
            <span className="icon is-left">
              <FontAwesomeIcon icon={faCalendar} />
            </span>
          </div>
        }
      />
      {error !== null && <p className="help is-danger">{error}</p>}
    </div>
  );
}

export default DateInput;
